/**
 * A file containing frontend API call protocols.
 * Intended to reduce code repetition and ease-of-use.
 */

import { getCookie } from '../../helpers';

//TODO Add comments for functions.
//TODO Comment in expected JSON return obj format for each Route.
//TODO Add functionality that prevents crashes if an error is caught or if something unexpected is returned.

async function attemptRefresh() {
  try {
    const res = await fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': getCookie('csrf_access_token')
      }
    });

    return res.ok; // true if refresh succeeded
  } catch (err) {
    console.error("Refresh request failed", err);
    return false;
  }
}

async function fetchWithAuthRetry(url, options = {}, retry = true) {
  const response = await fetch(url, {
    credentials: 'same-origin',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': getCookie('csrf_access_token'),
      ...(options.headers || {}),
    },
  });

  // If unauthorized and retry is allowed, attempt a refresh
  if (response.status === 401 && retry) {
    const refreshed = await attemptRefresh();

    if (refreshed) {
      // Retry original request one time
      return fetchWithAuthRetry(url, options, false);
    } else {
      // Redirect to login if refresh fails
      console.error("Session expired. Please log in again.");
      sessionStorage.setItem('isAuthenticated', false);
      window.location.href = "/login";
      return null;
    }
  }

  return response;
}

export async function logoutUser() {
    return await fetchWithAuthRetry('/api/auth/logout', {
        method: 'POST'
    })
    .then(response => response.json())
    .then(responseObj => {
        return responseObj;
    })
    .catch((error) => {
        console.error('Error:', error);
    });
}

export async function validateToken() {
  const cachedAuth = sessionStorage.getItem('isAuthenticated');

  if (cachedAuth !== null) {
    return cachedAuth === 'true'; // sessionStorage stores everything as strings
  }

  try {
    const response = await fetchWithAuthRetry('/api/auth/validate', {
      method: 'POST'
    });

    const isValid = response.status === 200;
    sessionStorage.setItem('isAuthenticated', isValid); // save for future
    return isValid;

  } catch (error) {
    console.error('Failed to validate token:', error);
    sessionStorage.setItem('isAuthenticated', false);
    return false;
  }
}

export async function fetchCreateSpace(name, description) {
    return await fetchWithAuthRetry('/api/users/me/spaces', {
        method: 'POST',
        body: JSON.stringify({
            name: name,
            description: description
        })
    })
    .then(response => response.json())
    .then(responseObj => {
        return responseObj;
    })
    .catch((error) => {
        console.error('Error:', error);
    });
}

export async function fetchUserSearches(searchSpace, page, rowsPerPage) {
    return await fetchWithAuthRetry(`/api/spaces/${searchSpace.id}/searches?page=${page}&limit=${rowsPerPage}`, {
        method: 'GET',
    })
    .then(response => response.json())
    .then(responseObj => {
        return responseObj;
    })
    .catch((error) => {
        console.error('Error:', error);
    });
}

export async function fetchUserSavedSearches(searchSpace, page, rowsPerPage) {
    return await fetchWithAuthRetry(`/api/spaces/${searchSpace.id}/saved-searches?page=${page}&limit=${rowsPerPage}`, {
        method: 'GET',

    })
    .then(response => response.json())
    .then(responseObj => {
        return responseObj;
    })
    .catch((error) => {
        console.error('Error:', error);
    });
}

export async function fetchSearchCount(searchSpace) {
    return await fetchWithAuthRetry(`/api/spaces/${searchSpace.id}/search-count`, {
        method: 'GET',
    })
    .then(response => response.json())
    .then(responseObj => {
        return responseObj;
    })
    .catch((error) => {
        console.error('Error:', error);
    });
}

export async function fetchSavedSearchCount(searchSpace) {
    return await fetchWithAuthRetry(`/api/spaces/${searchSpace.id}/saved-search-count`, {
        method: 'GET',
    })
    .then(response => response.json())
    .then(responseObj => {
        return responseObj;
    })
    .catch((error) => {
        console.error('Error:', error);
    });
}

export async function fetchDeleteSearch(taskId) {
    return await fetchWithAuthRetry(`/api/searches/${taskId}`, {
        method: 'DELETE',
    })
    .then(response => response.json())
    .then(responseObj => {
        return responseObj;
    })
    .catch((error) => {
        console.error('Error:', error);
    });
}

export async function fetchMoveSearch(taskId, selectedSpace) {
    return await fetchWithAuthRetry(`/api/searches/${taskId}/move`, {
        method: 'POST',
        body: JSON.stringify({
            space_id: selectedSpace.id
        })
    })
    .then(response => response.json())
    .catch((error) => {
        console.error('Error:', error);
    });
}

export async function fetchPinnedArticles(searchSpace) {
    const res = await fetchWithAuthRetry(`/api/spaces/${searchSpace.id}/pinned-articles`,{
        method: 'GET',
    })
    if (!res.ok) throw new Error(`Bad response: ${res.status}`);
    return await res.json();
}

export async function fetchSavedDocuments(searchSpace){
    const res = await fetchWithAuthRetry(`/api/spaces/${searchSpace.id}/documents`,{
        method: 'GET',
    })
    if (!res.ok) throw new Error(`Bad response: ${res.status}`);
    return await res.json();
}

export async function fetchRemoveArticleFromSpace(searchSpace, articleId) {
    return await fetchWithAuthRetry(`/api/spaces/${searchSpace.id}/article/${articleId}`,{
        method: 'DELETE',
    })
    .then(response => response.json())
    .catch((error) => {
        console.error('Error:', error);
    });
}

export async function fetchUserSearchSpaces(){
    return await fetchWithAuthRetry('/api/users/me/spaces', {
        method: 'GET',
    })
    .then(response => response.json())
    .catch((error) => {
        console.error('Error:', error);
    });
}

export async function fetchAddArticleToProject(searchSpace, article){
    return await fetchWithAuthRetry(`/api/articles/${article.id}/link`, {
        method: 'POST',
        body: JSON.stringify({
            space_id: searchSpace.id
        })
    })
    .then(response => response.json())
    .catch((error) => {
        console.error('Error:', error);
    });
}

export async function fetchRemoveArticleFromProject(searchSpace, article){
    return await fetchWithAuthRetry(`/api/spaces/${searchSpace.id}/articles/${article.id}`, {
        method: 'DELETE',
    })
    .then(response => response.json())
    .catch((error) => {
        console.error('Error:', error);
    });
}

export async function fetchRemoveDocumentFromProject(searchSpace, document){
    return await fetchWithAuthRetry(`/api/spaces/${searchSpace.id}/documents/${document.id}`, {
        method: 'DELETE',
    })
    .then(response => response.json())
    .catch((error) => {
        console.error('Error:', error);
    });
}

export async function fetchSaveAllArticlesFromSearch(searchId){
    return await fetchWithAuthRetry(`/api/searches/${searchId}/articles/save`, {
        method: 'POST',
    })
        .then(response => response.json())
        .catch((error) => {
            console.error('Error:', error);
        });
}

export async function fetchAddDocumentToProject(searchSpace, document){
    return await fetchWithAuthRetry(`/api/documents/${document.id}/link`, {
        method: 'POST',
        body: JSON.stringify({
            space_id: searchSpace.id,
        })
    })
    .then(response => response.json())
    .catch((error) => {
        console.error('Error:', error);
    });
}



export async function fetchAddDirectory(space, dirName, parentId = null) {
    try {
      if (!space || !dirName) {
        throw new Error('Missing space or dirName');
      }

      const requestBody = {
        name: dirName,
      };

      if (parentId !== null) {
        requestBody.parent_id = parentId;
      }

      const response = await fetchWithAuthRetry(`/api/spaces/${space.id}/directories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const contentType = response.headers.get('content-type');

      if (!response.ok) {
        const errorText = contentType && contentType.includes('application/json')
          ? (await response.json()).message
          : await response.text();
        throw new Error(`Server error for fetchAddDirectory request: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      return { success: true, data: result };
    } catch (error) {
      console.error('fetchAddDirectory error:', error.message || error);
      return { success: false, error: error.message || 'Unknown error occurred' };
    }
  }



  export async function fetchAddReference(selectedDirectoryId, referenceData) {
    try {
      const res = await fetchWithAuthRetry(`/api/directories/${selectedDirectoryId}/references`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(referenceData),
      });

      if (!res.ok) {
        const errorText = await res.text(); // read error as text to avoid .json() crash
        console.error(`API Error (${res.status}):`, errorText);
        throw new Error(`HTTP ${res.status}: ${errorText}`);
      }

      const responseData = await res.json();

      return responseData;
    } catch (error) {
      //these errors willl show in the browser's dev tools not the console
      console.error('Error adding reference:', error);
      throw error; // optionally rethrow so calling code can react
    }
  }


  export async function fetchUpdateReference(referenceData) {
    try {
      const res = await fetchWithAuthRetry(`/api/references/${referenceData.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(referenceData),
      });

      if (!res.ok) {
        const errorText = await res.text(); // read error as text to avoid .json() crash
        console.error(`API Error (${res.status}):`, errorText);
        throw new Error(`HTTP ${res.status}: ${errorText}`);
      }

      const responseData = await res.json();

      return responseData;
    } catch (error) {
      //these errors willl show in the browser's dev tools not the console
      console.error('Error updating reference:', error);
      throw error; // optionally rethrow so calling code can react
    }
  }


  export async function fetchDeleteDirectory(directoryId) {
    try {
      const res = await fetchWithAuthRetry(`/api/directories/${directoryId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: directoryId
        }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`HTTP ${res.status}: ${errorText}`);
      }

      return await res.json();
    } catch (error) {
      console.error('Error deleting directory:', error);
      throw error;
    }
  }

  export async function fetchDeleteReference(referenceId) {
    try {
      const response = await fetchWithAuthRetry(`/api/references/${referenceId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Delete failed (${response.status}):`, errorText);
        throw new Error(`Failed to delete reference: ${errorText}`);
      }

      const result = await response.json();
      return result;

    } catch (error) {
      console.error('Error in fetchDeleteReference:', error);
      throw error;
    }
  }

  export async function fetchAddArticleToReferences(space, article) {
    try {
      const response = await fetchWithAuthRetry(`/api/articles/${article.id}/convert`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          space_id: space.id,
        }),
      });

      if (!response.ok) {
        const errorText = response.headers.get('content-type')?.includes('application/json')
          ? (await response.json()).message
          : await response.text();
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      return { success: true, data: result };
    } catch (error) {
      console.error('Error adding article to references:', error.message || error);
      return { success: false, error: error.message || 'Unknown error occurred' };
    }
  }


export async function fetchGetDirectoryStructure(space) {
    return await fetchWithAuthRetry(`/api/spaces/${space.id}/directories/structure`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    .then(res => res.json())
    .then(json => {
        if (Array.isArray(json)) {
          return json; // ✅ good
        } else {
          console.warn('Unexpected response format from fetchGetDirectories json:', json);
          return []; // ⛑️ fail-safe
        }
      })
    .catch(err => {
      console.error('Error fetching directories:', err);
      return [];
    });
  }


export async function fetchGetSpaceReferences(space, directoryId = null){
    try {
      const response = await fetchWithAuthRetry(`/api/spaces/${space.id}/references`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch references');
      const data = await response.json();

      // If directoryId is provided, filter the results.  right now it never is
      if (directoryId !== null) {
        return data.filter(ref => ref.directory_id === directoryId);
      }

      return data;
    } catch (error) {
      console.error('Error fetching references:', error);
      return null;
    }
  };

export async function fetchMoveReference(referenceId, newDirectoryId) {
  try {
    const response = await fetchWithAuthRetry(`/api/references/${referenceId}/move`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        directory_id: newDirectoryId
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to move reference: ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error moving reference:', error);
    throw error;
  }
}
